import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { isEqual, map, pick } from 'lodash/fp';
import { Person } from '../../../../global/entities/Person';
import {
  deleteBuyOrderStore,
  saveEditAuditor,
  saveEditClientInfo,
  saveEditOwnerShares,
  saveEditProxies,
  saveEditRepresentatives,
} from '../buy-order-edit/buy-order-edit-redux';
import { CUSTOMER_FORM_ID } from '../../../../global/constants';
import {
  BuyOrderApiRelation,
  BuyOrderApiResponse,
} from '../buy-order-detailed/buy-order-detailed-types';
import { createCustomEntityAdapter } from '../../../../utils/redux/create-custom-entity-adapter';
import { getChangesBatch, personWithId } from './people-utils';

export type BuyOrderPersonEntity = Person & {
  personId?: string;
};

export const buyOrderPeopleAdapter = createCustomEntityAdapter<BuyOrderPersonEntity>({
  selectId: (x) => x.socialSecurityNumber!,
});

export const buyOrderPeopleSelectors = buyOrderPeopleAdapter.getSelectors();

const buyOrderPeopleSlice = createSlice({
  name: 'buyOrderPeople',
  initialState: {
    [CUSTOMER_FORM_ID]: {
      entities: {},
      ids: [],
    },
  } as Record<string, EntityState<BuyOrderPersonEntity>>,
  reducers: {
    initBuyOrderPeople(state, action: PayloadAction<{ id: string }>) {
      if (!state[action.payload.id]) {
        state[action.payload.id] = {
          entities: {},
          ids: [],
        };
      }
    },
    saveBuyOrderPeople(
      state,
      action: PayloadAction<{
        id: string;
        buyOrder?: BuyOrderApiResponse;
      }>,
    ) {
      const { buyOrder, id } = action.payload;

      function mapToPerson(relation: BuyOrderApiRelation) {
        return {
          firstName: relation.person.firstName,
          lastName: relation.person.lastName,
          socialSecurityNumber: relation.person.socialSecurityNumber,
          city: relation.person.city,
          address: relation.person.address,
          postalCode: relation.person.postalCode,
          personId: relation.personId,
        };
      }

      if (buyOrder) {
        if (buyOrder.auditor) {
          buyOrderPeopleAdapter.upsertOne(
            state[id],
            // Auditor has different format (not a person, resides in Auditor table in BE)
            // so we just do standard person pick properties
            personWithId(buyOrder.auditor),
          );
        }

        map(mapToPerson, buyOrder.relations).forEach((person) => {
          upsertPerson(state[id], person);
        });

        if (buyOrder.organizations) {
          buyOrder.organizations.forEach((organization) =>
            organization.signers.forEach((signer) => {
              // @ts-expect-error
              upsertPerson(state[id], mapToPerson(signer));
            }));
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveEditClientInfo, (state, action) => {
      if (!action.payload.socialSecurityNumber) return;

      buyOrderPeopleAdapter.upsertOne(state[action.payload.id], {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        socialSecurityNumber: action.payload.socialSecurityNumber,
        city: action.payload.city,
        address: action.payload.address,
        postalCode: action.payload.postalCode,
      });
    });

    builder.addCase(saveEditOwnerShares, (state, action) => {
      const { physicalPeople, id, legalShareholders } = action.payload;

      const legalPerson = pick([
        'socialSecurityNumber',
        'firstName',
        'lastName',
      ]);

      // Need to batch the changes to they dont override each other
      let changesBatch = getChangesBatch(state[id], physicalPeople || []);
      changesBatch = [
        ...changesBatch,
        ...getChangesBatch(
          state[id],
          legalShareholders || [],
          (a, b) => !isEqual(legalPerson(a), legalPerson(b)),
        ),
      ];

      changesBatch
        .map(personWithId)
        .forEach((person) => upsertPerson(state[id], person));
    });

    builder.addCase(saveEditRepresentatives, (state, action) => {
      const { representatives, ceo, id } = action.payload;

      const changesBatch = getChangesBatch(state[id], [
        ...(representatives ?? []),
        ceo,
      ]).map(personWithId);

      changesBatch.forEach((person) => upsertPerson(state[id], person));
    });

    builder.addCase(saveEditAuditor, (state, action) => {
      const { id, auditorPerson, auditorOption } = action.payload;

      if (auditorOption !== 'no' && auditorPerson) {
        upsertPerson(state[id], personWithId(auditorPerson));
      }
    });

    builder.addCase(saveEditProxies, (state, action) => {
      upsertPerson(state[action.payload.id], {
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        socialSecurityNumber: action.payload.socialSecurityNumber,
      });
    });

    builder.addCase(deleteBuyOrderStore, (state, action) => {
      state[action.payload.id] = {
        entities: {},
        ids: [],
      };
    });
  },
});

function upsertPerson(
  state: EntityState<BuyOrderPersonEntity>,
  person: Partial<Person>,
) {
  if (person.socialSecurityNumber) {
    buyOrderPeopleAdapter.patchOne(state, person);
  }
}

export const buyOrderPeopleReducer = buyOrderPeopleSlice.reducer;
export const { saveBuyOrderPeople, initBuyOrderPeople } = buyOrderPeopleSlice.actions;
